import styled from '@emotion/styled';
import { breakpoints, fontSize, Modal, palette, spacing } from '@playdapp/ui';
import { useMedia } from 'react-use';
import { useRouter } from 'next/router';

import { getBreakpointQuery, toPascalCase } from 'lib/util';
import { blockExplorerUrl } from 'lib/environment';
import type { NetworkName } from 'types/network';

import Button from '@/components/Button';
import Icon from '@/components/Icon';

export type ErrorMessage = {
  title: string;
  line1?: string;
  line2?: string;
};

type Props = {
  isOpen: boolean;
  handleOpen: () => void;
  type: 'error' | 'success';
  network?: NetworkName;
  txHash: string | null;
  errorDescription?: React.ReactNode;
  afterCloseRedirectTo?: string;
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxl};

  ${breakpoints.down('md')} {
    gap: ${spacing.l};
  }
`;

const ModalTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xxl};

  ${breakpoints.down('md')} {
    margin-bottom: ${spacing.s};
    gap: ${spacing.l};
  }
`;

const ModalTitle = styled.h3`
  font-size: ${fontSize.h3};
  font-weight: 900;
  text-align: center;

  ${breakpoints.down('md')} {
    font-size: ${fontSize.h4};
  }
`;

const ImageBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageBox = styled.figure`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: ${({ theme }) => theme.rem(120)};
  max-height: ${({ theme }) => theme.rem(120)};
  border-radius: 2.5rem;

  ${breakpoints.down('lg')} {
    max-width: ${({ theme }) => theme.rem(80)};
    max-height: ${({ theme }) => theme.rem(80)};
  }

  ${breakpoints.down('md')} {
    max-width: ${({ theme }) => theme.rem(48)};
    max-height: ${({ theme }) => theme.rem(48)};
  }
`;

const TextBlock = styled.div``;

const ModalDescription = styled.p`
  font-size: ${fontSize.p1};
  color: ${palette.gray900};
  text-align: center;

  ${breakpoints.down('lg')} {
    font-size: ${fontSize.p3};
  }

  ${breakpoints.down('md')} {
    font-size: ${fontSize.p4};
  }

  &:first-of-type {
    margin-top: ${spacing.s};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${spacing.s};

  a,
  button {
    width: 100%;
    max-width: 418px;
  }
`;

const TxHashBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${palette.gray900};
  font-size: ${fontSize.p4};

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing.s};
  }
`;

const TransactionResultModal = ({
  isOpen,
  handleOpen,
  type,
  network,
  txHash,
  errorDescription,
  afterCloseRedirectTo,
}: Props) => {
  const router = useRouter();
  const isLgDown = useMedia(getBreakpointQuery(breakpoints.down('lg')), false);

  const handleClose = () => {
    if (!afterCloseRedirectTo) {
      return;
    }
    if (/^https?:\/\//.test(afterCloseRedirectTo)) {
      window.open(afterCloseRedirectTo, '_blank');
      return;
    }
    router.push(afterCloseRedirectTo);
  };

  return (
    <Modal
      isOpen={isOpen}
      handleOpen={handleOpen}
      shouldCloseOnOverlayClick
      handleAfterClose={handleClose}
    >
      <ModalContent>
        <ModalTitleBlock>
          <ImageBlock>
            <ImageBox>
              <Icon name={`tx-${type}`} size={120} />
            </ImageBox>
          </ImageBlock>
          <TextBlock>
            <ModalTitle>
              {type === 'error' ? 'Error' : 'Your transaction succeed!'}
            </ModalTitle>
            {type === 'error' && !errorDescription && (
              <ModalDescription>
                An error has occurred.{isLgDown ? <br /> : ' '}Please contact
                &quot;Customer support&quot;.
              </ModalDescription>
            )}
            {type === 'error' && errorDescription && (
              <ModalDescription>{errorDescription}</ModalDescription>
            )}
          </TextBlock>
        </ModalTitleBlock>
        <ButtonGroup>
          <Button
            to={
              type === 'error'
                ? afterCloseRedirectTo ||
                  'https://playdapp.atlassian.net/servicedesk/customer/portals'
                : ''
            }
            onClick={handleOpen}
          >
            OK
          </Button>
        </ButtonGroup>
        {type === 'success' && network && txHash && network !== 'solana' && (
          <TxHashBlock>
            <a
              href={`${
                blockExplorerUrl[
                  process.env.NEXT_PUBLIC_ENV as 'development' | 'production'
                ][network]
              }/tx/${txHash}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on {toPascalCase(network)}scan <Icon name="link" size={18} />{' '}
            </a>
          </TxHashBlock>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TransactionResultModal;
